import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Search, Trash2, Ban, History } from "lucide-react";
import {
  ADDRESS_CHANNELS,
  errorMessage,
  fetchJson,
  formatDate,
  formatNumber,
  type Channel,
  type CommsContact,
  type Suppression,
} from "@/lib/comms";
import { EmptyState, ErrorState, StatusBadge, TableSkeleton } from "./shared";

interface ContactsResponse {
  contacts: CommsContact[];
  stats?: Record<string, unknown> | null;
}

const STATUS_OPTIONS = [
  { id: "all", label: "Any status" },
  { id: "subscribed", label: "Subscribed" },
  { id: "unsubscribed", label: "Unsubscribed" },
  { id: "bounced", label: "Bounced" },
  { id: "complained", label: "Complained" },
  { id: "invalid", label: "Invalid" },
];

const KIND_OPTIONS = [
  { id: "all", label: "Everyone" },
  { id: "parent", label: "Parents" },
  { id: "student", label: "Students" },
  { id: "lead", label: "Leads" },
];

function fullName(c: CommsContact): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return name || "No name on file";
}

function SuppressionsPanel() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<Channel>("email");
  const [address, setAddress] = useState("");
  const [reason, setReason] = useState("");
  const [removeTarget, setRemoveTarget] = useState<Suppression | null>(null);

  const { data, isLoading, error } = useQuery<{ suppressions: Suppression[] }>({
    queryKey: ["/api/admin/comms/suppressions"],
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/suppressions"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
  };

  const addMutation = useMutation({
    mutationFn: async (payload: { channel: Channel; address: string; reason?: string }) =>
      (await apiRequest("POST", "/api/admin/comms/suppressions", payload)).json(),
    onSuccess: () => {
      invalidate();
      setAddress("");
      setReason("");
      setOpen(false);
      toast({ title: "Suppression added", description: "That address will be skipped on every send." });
    },
    onError: (e: unknown) => toast({ title: "Could not add the suppression", description: errorMessage(e), variant: "destructive" }),
  });

  const removeMutation = useMutation({
    mutationFn: async (s: Suppression) =>
      (await apiRequest("DELETE", "/api/admin/comms/suppressions", { channel: s.channel, address: s.address })).json(),
    onSuccess: () => {
      invalidate();
      setRemoveTarget(null);
      toast({ title: "Suppression removed" });
    },
    onError: (e: unknown) => toast({ title: "Could not remove the suppression", description: errorMessage(e), variant: "destructive" }),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = address.trim();
    if (!trimmed) return;
    addMutation.mutate({ channel, address: trimmed, ...(reason.trim() ? { reason: reason.trim() } : {}) });
  };

  const suppressions = data?.suppressions || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Ban className="w-4 h-4" /> Suppressions</CardTitle>
            <CardDescription>
              Addresses blocked at send time whatever the segment says. Hard bounces and complaints land here automatically.
            </CardDescription>
          </div>
          <Button size="sm" variant={open ? "secondary" : "outline"} onClick={() => setOpen(o => !o)} data-testid="button-toggle-add-suppression">
            <Plus className="w-4 h-4 mr-2" /> Add suppression
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {open && (
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[140px_1fr_1fr_auto] items-end border rounded-md p-3">
            <div className="space-y-1.5">
              <Label htmlFor="suppression-channel">Channel</Label>
              <Select value={channel} onValueChange={v => setChannel(v as Channel)}>
                <SelectTrigger id="suppression-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADDRESS_CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="suppression-address">Address</Label>
              <Input
                id="suppression-address"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder={channel === "email" ? "parent@example.com" : "+18761234567"}
                data-testid="input-suppression-address"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="suppression-reason">Reason</Label>
              <Input
                id="suppression-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Optional, e.g. asked us to stop"
                data-testid="input-suppression-reason"
              />
            </div>
            <Button type="submit" disabled={addMutation.isPending || !address.trim()} data-testid="button-add-suppression">
              {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add
            </Button>
          </form>
        )}

        {error ? (
          <ErrorState error={error} />
        ) : isLoading ? (
          <TableSkeleton rows={3} cols={4} />
        ) : suppressions.length === 0 ? (
          <EmptyState title="Nothing suppressed" hint="No address has bounced, complained or been blocked by hand yet." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppressions.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium break-all">{s.address}</TableCell>
                    <TableCell><Badge variant="outline">{s.channel}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.reason || "Not recorded"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.source || "manual"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(s.createdAt)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemoveTarget(s)}
                        title="Remove suppression"
                        data-testid={`button-remove-suppression-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!removeTarget} onOpenChange={o => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock this address?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.address} will be eligible for {removeTarget?.channel} sends again. If it was suppressed for a hard bounce or a spam
              complaint, sending to it again puts the whole domain's reputation at risk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeTarget && removeMutation.mutate(removeTarget)}
              data-testid="button-confirm-remove-suppression"
            >
              {removeMutation.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function ContactsTab({ initialQuery = "", onOpenHistory }: { initialQuery?: string; onOpenHistory?: (contactId: string) => void } = {}) {
  const [search, setSearch] = useState(initialQuery);
  const [q, setQ] = useState(initialQuery.trim());
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, error } = useQuery<ContactsResponse>({
    queryKey: ["/api/admin/comms/contacts", q, kind, status],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("kind", kind);
      params.set("status", status);
      return fetchJson<ContactsResponse>(`/api/admin/comms/contacts?${params.toString()}`);
    },
  });

  const contacts = data?.contacts || [];

  const statEntries = useMemo(() => {
    const stats = data?.stats;
    if (!stats || typeof stats !== "object") return [] as Array<[string, number]>;
    return Object.entries(stats).filter(([, v]) => typeof v === "number") as Array<[string, number]>;
  }, [data]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Contact book</CardTitle>
          <CardDescription>
            One row per reachable person. Parents and students are separate contacts even when they share an account, because they are reached on
            different channels and consent separately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or phone"
                className="pl-9"
                aria-label="Search contacts"
                data-testid="input-search-contacts"
              />
            </div>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-full sm:w-[170px]" aria-label="Filter by contact kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {statEntries.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {statEntries.map(([key, value]) => (
                <span key={key}>
                  {key.replace(/_/g, " ")}: <span className="font-semibold text-foreground">{formatNumber(value)}</span>
                </span>
              ))}
            </div>
          )}

          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : contacts.length === 0 ? (
            <EmptyState
              title="No contacts match"
              hint={q || kind !== "all" || status !== "all"
                ? "Nothing fits this search and these filters. Widen them, or clear the search box."
                : "The contact book is empty. Run Refresh contacts and facts on the Overview tab to build it from the user table."}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Last email</TableHead>
                    <TableHead>Last engaged</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map(c => (
                    <TableRow key={c.id} data-testid={`row-contact-${c.id}`}>
                      <TableCell className="font-medium">{fullName(c)}</TableCell>
                      <TableCell><div className="flex flex-col gap-1"><Badge variant="outline" className="w-fit">{c.kind}</Badge>{c.tags && c.tags.length > 0 && <span className="text-[10px] text-muted-foreground max-w-[160px] truncate">{c.tags.join(", ")}</span>}</div></TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm break-all">{c.email || "No email"}</span>
                          {c.email ? <span><StatusBadge status={c.emailStatus} /></span> : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm whitespace-nowrap">{c.phone || "No phone"}</span>
                          {c.phone ? <span><StatusBadge status={c.whatsappStatus} /></span> : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(c.lastEmailAt)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(c.lastEngagedAt)}</TableCell>
                      <TableCell className="text-right">
                        {onOpenHistory && <Button size="sm" variant="ghost" onClick={() => onOpenHistory(c.id)} title="Comms history" data-testid={`button-history-${c.id}`}><History className="w-4 h-4" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground pt-3">{formatNumber(contacts.length)} contact{contacts.length === 1 ? "" : "s"} shown.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <SuppressionsPanel />
    </div>
  );
}
